import { changeLanguage } from "@/helpers/languageHelpers"
import { isSilentAuthAllowed } from "@/helpers/authenticationHelpers"
import { getParameter, getParameterAsInt, getQueryParameterAsInt } from "@/helpers/dataHelpers"
import { getAuthenticationStore, getCommonStore, getContractStore, getCustomerStore, getRecoveryStore } from "@/helpers/storeHelpers"
import { WritableComputedRef } from "vue"
import { useNewOrderStore } from "@/stores/newOrder"
import { RouteLocationNormalized } from "vue-router"
import { AuthStore } from "@/stores/authentication"
import { IRoute } from "@/interface"
import Notify from 'quasar/src/plugins/Notify.js';

import i18n from "@/locales"

// Navigation guards 

export const activateOrderGuard = (to: RouteLocationNormalized, _from: RouteLocationNormalized) => {
  const authStore = getAuthenticationStore()
  if (authStore && authStore.isAuthenticated && !authStore.isUser) {
    // We don't care about the parameter if authenticated.
    return { name: 'order-follow'}
  }

  return true
}

export const checkContractIdGuard = (to: RouteLocationNormalized, _from: RouteLocationNormalized) => {
    const contractStore = getContractStore() 
    const authStore = getAuthenticationStore()
    const param = getParameterAsInt(to.params.id)
    if (!contractStore || !contractStore.existContract(param)) {
      return { name: "not-found" }
    }
    if (authStore && authStore.isUser && authStore.msisdn !== contractStore.findContract(param)?.msisdn) {
      return { name: "not-found" }
    }
    
    return true
}

export const checkContractGuard = (_to: RouteLocationNormalized, _from: RouteLocationNormalized) => {
  const authStore = getAuthenticationStore()
  if (authStore && authStore.isAuthenticated && !authStore.isUser) {
    // We don't care about the parameter if authenticated.
    return true
  }

  return { name: "not-found" }
}

export const checkUserRoleGuard = (_to: RouteLocationNormalized, _from: RouteLocationNormalized) => {
  const authStore = getAuthenticationStore()
  if (!authStore || authStore.isUser) {
    return { name: "not-found" }
  }
  
  return true
}

export const confirmEmailGuard = async (to: RouteLocationNormalized, _from: RouteLocationNormalized) => {
  const uuid: string = getParameter(to.params.uuid) 
  const token: string = getParameter(to.params.token)
  const authStore = getAuthenticationStore()
  const customerStore = getCustomerStore()
  const isConfirmed = customerStore ? await customerStore.confirmEmail(uuid, token) : true
  if (isConfirmed) {
    Notify.create({
      type: 'positive',
      message: `${i18n.global.t('login.email_confirmed')}`,
      position: 'top-right',
      html: true,
      progress: true
    })
  }
  if (authStore && authStore.isAuthenticated) {
    if (customerStore && customerStore.contact && customerStore.guid != '' && customerStore.guid === uuid) {
      // Update the confirmation email status 
      customerStore.contact.isEmailConfirmed = isConfirmed
    }

    return { name: "home" }
  } 

  return { name: "login-main" }
}

export const enterRenewAccessGuard = async (_to: RouteLocationNormalized, _from: RouteLocationNormalized) => {
  const authStore = getAuthenticationStore()
  if (authStore) {
    if (isSilentAuthAllowed() && !authStore.isAuthenticated && authStore.refresh_token && authStore.user?.guid) {
      return true
    } 
    if (authStore.isAuthenticated) {
      return { name: 'home' }
    }
  
    return { name: 'login-main' }
  }

  return { name: 'login-main' }
}

export const isItemAccessible = (item: IRoute|Omit<IRoute, 'content'>) => {
  const authStore = getAuthenticationStore();
  return authStore && (!item.roles || item.roles.includes(authStore.user?.role ?? ''));
}

export const loginUuidGuard = async (to: RouteLocationNormalized, _from: RouteLocationNormalized) => {
  const commonStore = getCommonStore()
  const authStore = getAuthenticationStore()
  const uuid: string = getParameter(to.params.uuid) 
  const token: string = getParameter(to.params.token)

  commonStore?.startLoading(true, true)
  authStore?.logout() // Forces a logout.
  if (authStore) {
    return authStore.loginUuid(uuid, token).then((response) => {
      if (commonStore) {
        commonStore.stopLoading()
      }
      if (response == 200 && authStore.isAuthenticated) {
        if (authStore.lang && authStore.lang !== i18n.global.locale) {
          // Forces the user Language.
          changeLanguage(authStore, authStore.lang, (i18n.global.locale as unknown) as WritableComputedRef<string>, false)
        }

        // Get contract ID from redirection.
        const contractId: number = getQueryParameterAsInt(to.query.contractId)
        if (contractId > 0 && commonStore) {
          commonStore.forceContractId = contractId
        }
  
        if (to.query.redirect) {
          return { path: to.query.redirect }
        } else {
          return { name: "home" }
        }
      } 
      
      return { name: "login-main" }
    })
  } 

  return { name: "login-main" }
}

export const optimizeSubscriptionGuard = async (_to: RouteLocationNormalized, _from: RouteLocationNormalized) => {
  const contractStore = getContractStore()
  const newOrders = useNewOrderStore()
  if (!contractStore || !contractStore.currentContract) {
    return { name: "home" }
  }
  if (contractStore.hasAtLeastOneBlockage || contractStore.isModificationRestricted && newOrders.context !== 'SeeClosedOrder') {
    return { name: "subscription-main"}
  }

  return true
}

export const recoverPasswordEmailGuard = async (to: RouteLocationNormalized, _from: RouteLocationNormalized) => {
    const commonStore = getCommonStore()
    const recoveryStore = getRecoveryStore() 
    const uuid: string = getParameter(to.params.uuid) 
    const token: string = getParameter(to.params.token)
    const isCreate = !!to.name?.toString()?.match(/create/)?.length

    commonStore?.startLoading(true, true)
    if (recoveryStore) {
      return recoveryStore.initRecovery(uuid, token, isCreate).catch(function (error) {
        console.error("An error occurred", error)
      }).finally(function () {
        commonStore?.stopLoading()
        return true
      })
    }

    return true
}

export const redirectToRenewIfPossible = (options: {fallBackToLogin?: boolean} = {}, authStore: AuthStore|null = null) => {
  const store = authStore ?? getAuthenticationStore()
  if (!store) {
    return null
  }
  if (isSilentAuthAllowed() && store.refresh_token !== '' && store.user?.guid) {
    return { name: 'renew-access' }
  } else if (options.fallBackToLogin) {
    return { name: 'login-main' }
  }
  
  return null
}